import React, { useEffect, useState } from "react";
import { Command } from "cmdk";
import { router } from "@inertiajs/react";
import { useHotkeys } from "react-hotkeys-hook";
import {
    Briefcase,
    Building2,
    Kanban,
    LayoutDashboard,
    ListChecks,
    Search,
    UserPlus,
    UserRound,
} from "lucide-react";

const STATIC_ACTIONS = [
    { id: "crm-dashboard", label: "لوحة تحكم CRM", icon: LayoutDashboard, href: "/dashboard/crm" },
    { id: "crm-companies", label: "الشركات", icon: Building2, href: "/dashboard/crm/companies" },
    { id: "crm-contacts", label: "جهات الاتصال", icon: UserRound, href: "/dashboard/crm/contacts" },
    { id: "crm-deals", label: "الصفقات", icon: Kanban, href: "/dashboard/crm/deals" },
    { id: "crm-tasks", label: "المهام", icon: ListChecks, href: "/dashboard/crm/tasks" },
    { id: "new-company", label: "شركة جديدة", icon: Building2, href: "/dashboard/crm/companies?new=1" },
    { id: "new-contact", label: "جهة اتصال جديدة", icon: UserPlus, href: "/dashboard/crm/contacts?new=1" },
    { id: "new-deal", label: "صفقة جديدة", icon: Briefcase, href: "/dashboard/crm/deals?new=1" },
    { id: "new-task", label: "مهمة جديدة", icon: ListChecks, href: "/dashboard/crm/tasks?new=1" },
];

const RESULT_ICONS = {
    company: Building2,
    contact: UserRound,
    deal: Briefcase,
    task: ListChecks,
};

/**
 * Global ⌘K / Ctrl+K command palette. Mounted once in DashboardLayout.
 * Static navigation/quick-create actions are always available; live search
 * results come from GET /dashboard/crm/search (see SearchController) and
 * are fetched defensively — a missing/erroring endpoint just yields no
 * results rather than breaking the palette.
 */
export default function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);

    useHotkeys("meta+k, ctrl+k", (e) => {
        e.preventDefault();
        setOpen((v) => !v);
    });

    useEffect(() => {
        if (!open) {
            setQuery("");
            setResults([]);
        }
    }, [open]);

    useEffect(() => {
        const openPalette = () => setOpen(true);
        window.addEventListener("open-command-palette", openPalette);
        return () => window.removeEventListener("open-command-palette", openPalette);
    }, []);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => {
            fetch(`/dashboard/crm/search?q=${encodeURIComponent(query)}`, {
                headers: { Accept: "application/json" },
                signal: controller.signal,
            })
                .then((res) => (res.ok ? res.json() : { results: [] }))
                .then((data) => setResults(data.results || []))
                .catch(() => setResults([]));
        }, 200);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query]);

    const go = (href) => {
        setOpen(false);
        router.visit(href);
    };

    return (
        <Command.Dialog
            open={open}
            onOpenChange={setOpen}
            label="بحث سريع"
            className="fixed inset-x-0 top-24 z-[1200] mx-auto w-11/12 max-w-xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        >
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                <Search className="h-4.5 w-4.5 text-gray-400 shrink-0" />
                <Command.Input
                    value={query}
                    onValueChange={setQuery}
                    placeholder="ابحث أو اكتب أمرًا..."
                    className="w-full text-sm outline-none placeholder:text-gray-400"
                />
                <kbd className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-400">esc</kbd>
            </div>
            <Command.List className="max-h-96 overflow-y-auto p-2">
                <Command.Empty className="py-8 text-center text-sm text-gray-400">لا نتائج</Command.Empty>

                {results.length > 0 && (
                    <Command.Group heading="نتائج البحث" className="px-2 py-1 text-xs font-medium text-gray-400">
                        {results.map((r) => {
                            const Icon = RESULT_ICONS[r.type] || Search;
                            return (
                                <Command.Item
                                    key={`${r.type}-${r.id}`}
                                    value={`${r.type}-${r.id}-${r.label}`}
                                    onSelect={() => go(r.href)}
                                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 aria-selected:bg-primary-50 aria-selected:text-primary-700"
                                >
                                    <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                                    <span className="truncate">{r.label}</span>
                                    {r.sublabel && <span className="truncate text-xs text-gray-400">{r.sublabel}</span>}
                                </Command.Item>
                            );
                        })}
                    </Command.Group>
                )}

                <Command.Group heading="التنقل والإجراءات" className="px-2 py-1 text-xs font-medium text-gray-400">
                    {STATIC_ACTIONS.map((action) => (
                        <Command.Item
                            key={action.id}
                            value={`${action.id}-${action.label}`}
                            onSelect={() => go(action.href)}
                            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 aria-selected:bg-primary-50 aria-selected:text-primary-700"
                        >
                            <action.icon className="h-4 w-4 shrink-0 text-gray-400" />
                            {action.label}
                        </Command.Item>
                    ))}
                </Command.Group>
            </Command.List>
        </Command.Dialog>
    );
}
